import {
  forwardRef,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { flushSync } from "react-dom";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { formatPercent, TONE_CLASSES } from "./format";
import { TrendLayoutContext } from "./trendLayout";

interface YoYTrendProps {
  value: number;
  /** The percent and its "YoY" label on one line, instead of one above the other. */
  inline?: boolean;
  className?: string;
  dataTest?: string;
}

/** Year-over-year change: an arrow and the percent, green up and red down; zero shows no arrow. */
export const YoYTrend = forwardRef<HTMLDivElement, YoYTrendProps>(
  ({ value, inline, className, dataTest = "kpi-yoy" }, ref) => {
    const { t } = useTranslation();
    // Shown as a whole percent; tone and arrow follow that figure, so a change
    // that shows as "0%" carries neither.
    const shown = Math.round(value);
    const tone = shown > 0 ? "positive" : shown < 0 ? "negative" : "neutral";
    return (
      <div
        ref={ref}
        data-test={dataTest}
        data-layout={inline ? "inline" : "stacked"}
        className={cn(
          "flex min-w-0",
          // Inline, "YoY" wraps under the percent only when even one line does not fit.
          inline ? "flex-row flex-wrap items-center gap-x-1.5" : "flex-col items-start",
          className
        )}
      >
        <div
          className={cn(
            "flex min-w-0 max-w-full items-center",
            !inline && "gap-0.5",
            TONE_CLASSES[tone]
          )}
        >
          {shown !== 0 && (
            <IconContainer size="sm" color="current">
              {shown > 0 ? <ArrowUpRight /> : <ArrowDownRight />}
            </IconContainer>
          )}
          <Text
            as="span"
            variant="smBody"
            className={cn(
              "whitespace-nowrap",
              inline ? "font-normal" : "font-medium",
              TONE_CLASSES[tone]
            )}
          >
            {formatPercent(Math.abs(shown), 0)}
          </Text>
        </div>
        <Text as="span" variant="smBodySecondary" className="whitespace-nowrap">
          {t("kpisV2.yoy")}
        </Text>
      </div>
    );
  }
);
YoYTrend.displayName = "YoYTrend";

/**
 * Whether the trend, stacked, fits beside the figure. It is measured rather than
 * left to a container query because the answer depends on how wide the value
 * and the trend are, not on the card alone. Before anything is measured it
 * reports a fit, the layout the card had before.
 */
const useTrendFitsBeside = (hasTrend: boolean) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const figureRef = useRef<HTMLDivElement>(null);
  const stackedCopyRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(true);

  const measure = useCallback(() => {
    const root = rootRef.current;
    const figure = figureRef.current;
    const copy = stackedCopyRef.current;
    const available = root?.getBoundingClientRect().width ?? 0;
    if (!root || !figure || !copy || available === 0) {
      setFits(true);
      return;
    }
    const row = figure.parentElement;
    const gap = row ? parseFloat(getComputedStyle(row).columnGap) || 0 : 0;
    // Measured on the hidden copy, which is always stacked: the inline trend is
    // narrower, so measuring the visible one would flip the layout back and forth.
    const stacked = Math.max(
      0,
      ...Array.from(copy.children, (child) => child.getBoundingClientRect().width)
    );
    // Half a pixel of slack absorbs sub-pixel rounding.
    setFits(figure.getBoundingClientRect().width + gap + stacked <= available + 0.5);
  }, []);

  // Every render, since a new value or trend changes the widths the answer rests on.
  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const copy = stackedCopyRef.current;
    // Observer callbacks run before paint; rendering the result there keeps a
    // resize from painting one frame of the old layout.
    const observer = new ResizeObserver(() => flushSync(measure));
    for (const element of [rootRef.current, figureRef.current, copy]) {
      if (element) observer.observe(element);
    }
    if (copy) for (const child of Array.from(copy.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [hasTrend, measure]);

  return { rootRef, figureRef, stackedCopyRef, fits };
};

interface KPIValueProps {
  value: string;
  label?: string;
  /** Percent change against last year; the trend is hidden when it is absent. */
  yoy?: number | null;
  /** The smaller label of the narrow cards. */
  compactLabel?: boolean;
  dataTest: string;
}

/**
 * One figure on a card: the value with its label below, and the YoY trend beside it.
 * Neither the value nor the trend truncates. Below 185px wide the value steps
 * down a size and sits closer to the trend. When the trend still does not fit
 * beside the value, it moves below the label as one line, the arrow and percent
 * beside "YoY". A compact label wraps onto a second line instead.
 */
const KPIValue = ({ value, label, yoy, compactLabel, dataTest }: KPIValueProps) => {
  const hasTrend = yoy != null;
  const { rootRef, figureRef, stackedCopyRef, fits: ownFits } =
    useTrendFitsBeside(hasTrend);
  const cardLayout = useContext(TrendLayoutContext);
  const report = cardLayout?.report;
  const reportKey = useId();
  useLayoutEffect(() => {
    report?.(reportKey, ownFits || !hasTrend);
    // A figure that is gone no longer keeps the card's other trends below.
    return () => report?.(reportKey, true);
  }, [report, reportKey, ownFits, hasTrend]);
  const fits = cardLayout ? cardLayout.allFit : ownFits;
  return (
    <div
      ref={rootRef}
      data-test={dataTest}
      className="relative flex-1 min-w-0 [container-type:inline-size]"
    >
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1 [@container(min-width:185px)]:gap-x-4">
        <div
          ref={figureRef}
          className={cn("flex flex-col", compactLabel ? "min-w-0" : "shrink-0")}
        >
          <Text
            as="div"
            data-test={`${dataTest}-value`}
            variant="title2H1"
            className="whitespace-nowrap text-2xl leading-7 [@container(min-width:185px)]:text-3xl [@container(min-width:185px)]:leading-9"
          >
            {value}
          </Text>
          {label && (
            <Text
              as="div"
              data-test={`${dataTest}-label`}
              variant={compactLabel ? "smBodySecondary" : "bodySecondary"}
              className={compactLabel ? undefined : "whitespace-nowrap"}
            >
              {label}
            </Text>
          )}
        </div>
        {hasTrend && (
          <YoYTrend
            value={yoy}
            inline={!fits}
            className={fits ? undefined : "basis-full"}
          />
        )}
      </div>
      {hasTrend && (
        <div aria-hidden className="pointer-events-none invisible absolute left-0 top-0 w-max">
          <YoYTrend ref={stackedCopyRef} value={yoy} dataTest="kpi-yoy-measure" />
        </div>
      )}
    </div>
  );
};

export default KPIValue;
